/**
 * JS hooks para una <transition> que anima la altura de un sub-list tipo
 * acordeón. Mide `scrollHeight` al entrar para animar 0 → altura real, y
 * reverso al salir. Después del enter, libera `height` a `auto` para que
 * el sub-list se ajuste si su contenido cambia mientras está visible.
 *
 * Uso (en setup):
 *   const { onBeforeEnter, onEnter, onAfterEnter, onLeave } =
 *       useAccordionTransition();
 *
 * Y en template:
 *   <transition
 *     name="accordion"
 *     @before-enter="onBeforeEnter"
 *     @enter="onEnter"
 *     @after-enter="onAfterEnter"
 *     @leave="onLeave"
 *   >
 *     <ul v-if="open">...</ul>
 *   </transition>
 *
 * El consumidor define la CSS (height/opacity) con el timing que prefiera:
 *   .accordion-enter-active,
 *   .accordion-leave-active {
 *     transition: height var(--app-transition-base),
 *                 opacity var(--app-transition-base);
 *   }
 */
export function useAccordionTransition() {
    function onBeforeEnter(el) {
        el.style.height = "0";
        el.style.opacity = "0";
    }

    function onEnter(el, done) {
        // Force reflow para que el frame inicial con height:0 pinte antes de
        // arrancar la transición a scrollHeight.
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight;
        el.style.height = el.scrollHeight + "px";
        el.style.opacity = "1";
        const onEnd = () => {
            el.removeEventListener("transitionend", onEnd);
            done();
        };
        el.addEventListener("transitionend", onEnd);
    }

    function onAfterEnter(el) {
        // Liberamos height a auto para que el sublist se ajuste si su
        // contenido cambia mientras está visible.
        el.style.height = "auto";
    }

    function onLeave(el, done) {
        el.style.height = el.scrollHeight + "px";
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight;
        el.style.height = "0";
        el.style.opacity = "0";
        const onEnd = () => {
            el.removeEventListener("transitionend", onEnd);
            done();
        };
        el.addEventListener("transitionend", onEnd);
    }

    return { onBeforeEnter, onEnter, onAfterEnter, onLeave };
}
